import { UiAction, UiActionKeys } from '../actions/ui';
import { SpreadBackgroundAction, SpreadBackgroundActionKeys } from '../actions/spreadBackground';
import { EditorType, ZoomType } from '../constants';
import { ZOOM_STEP } from './constants';
import { getRoundedValue } from '../utils/helpers';

export interface LabelState {
	label: string;
	isError: boolean;
	isRenaming: boolean;
}

export interface EditPanelState {
	isDisplayed: boolean;
	x: number | null;
	y: number | null;
}

export interface ActiveItemState {
	id: string;
	page: number | null;
	parentId: string | null;
}

export interface LockedEditorPopupState {
	isDisplayed: boolean;
	userName: string;
}

const labelInitialState: LabelState = {
	label: '',
	isError: false,
	isRenaming: false,
};

const editPanelInitialState: EditPanelState = {
	isDisplayed: false,
	x: null,
	y: null,
};

const activeItemInitialState: ActiveItemState = {
	id: '',
	page: null,
	parentId: null,
};

const lockedEditorPopupInitialState: LockedEditorPopupState = {
	isDisplayed: false,
	userName: '',
};

export const type = (state: EditorType = EditorType.HTML, action: UiAction): EditorType => {
	switch (action.type) {
		case UiActionKeys.SET_EDITOR_TYPE:
			return action.editorType;
		default:
			return state;
	}
};

export const label = (state: LabelState = labelInitialState, action: UiAction): LabelState => {
	switch (action.type) {
		case UiActionKeys.SET_LABEL:
			return {
				...state,
				label: action.label,
			};
		case UiActionKeys.SET_IS_RENAMING:
			return {
				...state,
				isRenaming: action.isRenaming,
			};
		case UiActionKeys.RENAME:
			return {
				...state,
				label: action.newLabel,
				isRenaming: false,
			};
		case UiActionKeys.SET_RENAME_ERROR:
			return {
				...state,
				isError: action.isError,
			};
		case UiActionKeys.RESET:
			return labelInitialState;
		default:
			return state;
	}
};

export const isEditorLoaded = (state: boolean = false, action: UiAction): boolean => {
	switch (action.type) {
		case UiActionKeys.SET_IS_EDITOR_LOADED:
			return action.isLoaded;
		case UiActionKeys.RESET:
			return false;
		default:
			return state;
	}
};

export const editPanel = (state: EditPanelState = editPanelInitialState, action: UiAction | SpreadBackgroundAction): EditPanelState => {
	switch (action.type) {
		case UiActionKeys.TOGGLE_EDIT_PANEL:
			return {
				...state,
				isDisplayed: !state.isDisplayed,
			};
		case UiActionKeys.SET_ACTIVE_ITEM:
			return {
				...state,
				x: null,
				y: null,
			};
		case UiActionKeys.RESET_ACTIVE_ITEM:
		case UiActionKeys.SHOW_LOCKED_EDITOR_POPUP:
			return {
				...state,
				isDisplayed: false,
				x: null,
				y: null,
			};
		case SpreadBackgroundActionKeys.SET_IS_SPREAD_BACKGROUND_ACTIVE:
			return {
				...state,
				isDisplayed: action.isActive,
			};
		case UiActionKeys.MOVE_EDIT_PANEL:
			return {
				...state,
				x: action.x,
				y: action.y,
			};
		case UiActionKeys.RESET:
			return editPanelInitialState;
		default:
			return state;
	}
};

export const activeItem = (state: ActiveItemState = activeItemInitialState, action: UiAction): ActiveItemState => {
	switch (action.type) {
		case UiActionKeys.SET_ACTIVE_ITEM: {
			const { id, page, parentId } = action;
			return {
				...state,
				id,
				page,
				parentId,
			};
		}
		case UiActionKeys.RESET_ACTIVE_ITEM:
		case UiActionKeys.RESET:
			return activeItemInitialState;
		default:
			return state;
	}
};

export const version = (state: number = 0, action: UiAction): number => {
	switch (action.type) {
		case UiActionKeys.SET_VERSION:
			return action.version;
		default:
			return state;
	}
};

export const spreadZoom = (state: number = 1, action: UiAction): number => {
	switch (action.type) {
		case UiActionKeys.ZOOM_PAGE: {
			if (action.zoom === ZoomType.FIT) {
				return 1;
			} else {
				const step = action.zoom === ZoomType.IN ? ZOOM_STEP : -ZOOM_STEP;
				const newValue = getRoundedValue(state += step, 10);
				return newValue < ZOOM_STEP ? ZOOM_STEP : newValue;
			}
		}
		case UiActionKeys.RESET:
			return 1;
		default:
			return state;
	}
};

export const lockedEditorPopup = (state: LockedEditorPopupState = lockedEditorPopupInitialState, action: UiAction): LockedEditorPopupState => {
	switch (action.type) {
		case UiActionKeys.SHOW_LOCKED_EDITOR_POPUP:
			return {
				...state,
				isDisplayed: true,
				userName: action.userName,
			};
		case UiActionKeys.HIDE_LOCKED_EDITOR_POPUP:
		case UiActionKeys.RESET:
			return lockedEditorPopupInitialState;
		default:
			return state;
	}
};

export const isAccessibleVersion = (state: boolean = false, action: UiAction): boolean => {
	switch (action.type) {
		case UiActionKeys.SET_IS_ACCESSIBLE_VERSION:
			return action.isAccessibleVersion;
		default:
			return state;
	}
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/reducer/ui.ts